/**
 * Which requests are big enough to need a roster read (VE-32420).
 *
 * THE LOAD SEQUENCE THIS SERVES. Opening a request fetches its first page of
 * rows with the full per-row selection. That page is both the data AND the
 * measurement: if it comes back SHORT, the request holds fewer files than a
 * page and that one query was the whole thing — fully loaded, no roster
 * needed. If it comes back FULL, there are more, and the roster walk starts.
 *
 * Most requests are small, so most pay exactly one query.
 *
 * WHAT KNOWING BUYS IS PARALLELISM, NOT A SKIPPED QUERY. Page one is fetched
 * by `fetchSubFolders`, which answers the same `SET_SELECTED_FOLDER_SUCCESS`
 * independently — so a known-large request still pays for it. What the entry
 * removes is the WAIT: on a first visit the roster cannot start until page one
 * comes back and is measured, while on a later visit it starts at once,
 * alongside it. On a large request that is the difference between two
 * round-trips and one.
 *
 * WRONG IN EITHER DIRECTION IS SAFE, which is why nothing has to invalidate it
 * when files are added or removed:
 *
 * - Stale "large" on a request that shrank → a roster read that was not
 *   needed. It is the cheap query; the list is correct either way.
 * - Stale "small" (absent) on a request that grew → the page comes back full
 *   and the roster starts, exactly as it would have on a first visit.
 *
 * So entries are only ever written, never checked for freshness, and a count
 * that is out of date costs at most one query.
 *
 * LOCALSTORAGE IS DELIBERATELY A STOPGAP. This whole dance exists because
 * `TDOList` reports no total — `count` is the size of the response, not of the
 * collection. `IngestSlugList` already carries a `totalCount`, and the
 * notification mailboxes carry one explicitly so clients "don't have to page
 * through to get that information". If `childTDOs` gains the same, the first
 * response reports the size directly and this file is deleted rather than
 * migrated somewhere shared. Per-device is an acceptable price for something
 * built to be thrown away; a shared store would be a mirror needing upkeep on
 * every add and remove.
 */

const STORAGE_KEY = 'redact.largeRequests.v1';

/** Entries kept, newest first. Bounds the key against a long-lived browser. */
const MAX_ENTRIES = 200;

type LargeRequests = Record<string, number>;

const read = (): LargeRequests => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as LargeRequests;
  } catch {
    // Unreadable or unparseable — treat as "nothing known", which costs one
    // page-one measurement and nothing else.
    return {};
  }
};

/**
 * Is this request known to hold more files than one page?
 *
 * A `false` means "not known to be", never "known not to be" — an unvisited
 * request and a small one are the same answer here, and both are handled by
 * measuring page one.
 */
export const isKnownLargeRequest = (folderId: string | undefined): boolean =>
  !!folderId && typeof read()[folderId] === 'number';

/**
 * Record that a request is large, and roughly how large.
 *
 * The count is advisory — it is whatever the last roster read saw, and files
 * move. Nothing branches on the NUMBER; only on the entry existing at all.
 * It is stored because a size is worth having when diagnosing a slow request,
 * and costs nothing beside the key it is already writing.
 */
export const rememberLargeRequest = (
  folderId: string | undefined,
  fileCount: number
): void => {
  if (!folderId) {
    return;
  }
  try {
    const current = read();
    // The literal leads so a request already present keeps its position and
    // only has its count refreshed; the explicit set is what refreshes it,
    // since the spread would otherwise restore the stored value.
    const next: LargeRequests = { [folderId]: fileCount, ...current };
    next[folderId] = fileCount;
    const trimmed = Object.fromEntries(
      Object.entries(next).slice(0, MAX_ENTRIES)
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Best-effort. Without it a large request simply measures page one again
    // on its next visit, which is the uncached path and still correct.
  }
};
